import { useRef, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

/**
 * Utility hook for pages that manage their own search state (query + filters)
 * via a specialised data hook (e.g. useDistricts, useChurches) rather than
 * the generic `useSearch` hook.
 *
 * Does two things:
 *  1. Returns `initialQuery` and `initialFilters` read from the URL on first
 *     render so the page can seed its own state.
 *  2. Syncs the caller's current state back to the URL (replace, no history push)
 *     whenever `query`, `filters`, or `page` change.
 *
 * Usage:
 *
 *   const { initialQuery, initialFilters } = useSearchUrlSync({
 *     query: debouncedQuery,
 *     filters: activeFilters,
 *     page: currentPage,
 *   });
 *
 *   // Then seed your local state:
 *   const [query, setQuery] = useState(initialQuery);
 *   const [filters, setFilters] = useState(initialFilters);
 */

interface UseSearchUrlSyncOptions {
  /** The debounced (settled) query value — not the raw keystroke value. */
  query: string;
  /** Currently-applied filter values. */
  filters: Record<string, any>;
  /** Current page number. */
  page: number;
}

interface UseSearchUrlSyncReturn {
  /** Value of the `q` URL param on first render (for seeding local query state). */
  initialQuery: string;
  /** Non-`q`/`page` URL params on first render (for seeding local filter state). */
  initialFilters: Record<string, any>;
  /** Current page from URL on first render. */
  initialPage: number;
}

function getFiltersFromSearchParams(searchParams: URLSearchParams): Record<string, any> {
  const result: Record<string, any> = {};
  searchParams.forEach((value, key) => {
    if (key !== 'q' && key !== 'page') result[key] = value;
  });
  return result;
}

export function useSearchUrlSync({
  query,
  filters,
  page,
}: UseSearchUrlSyncOptions): UseSearchUrlSyncReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // ─── Capture initial values from URL on mount ──────────────────────────
  // These refs are frozen after the first render — they're only used to seed
  // the caller's state once and never change.
  const initialQueryRef = useRef<string>(searchParams.get('q') ?? '');
  const initialPageRef = useRef<number>(Number(searchParams.get('page') || '1'));
  const initialFiltersRef = useRef<Record<string, any>>(getFiltersFromSearchParams(searchParams));

  // ─── Sync state → URL ──────────────────────────────────────────────────
  const prevFilterKeysRef = useRef<Set<string>>(new Set());
  const lastSyncedRef = useRef('');
  // Skip the very first render (URL already has the right values).
  const isMountRef = useRef(true);
  const previousPathnameRef = useRef(location.pathname);

  useEffect(() => {
    if (previousPathnameRef.current === location.pathname) return;

    previousPathnameRef.current = location.pathname;
    prevFilterKeysRef.current = new Set();
    lastSyncedRef.current = '';
    isMountRef.current = true;
  }, [location.pathname]);

  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }

    const nextParams: Record<string, string> = {};
    if (query) nextParams.q = query;
    if (page > 1) nextParams.page = String(page);

    const nextFilterKeys = new Set<string>();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        nextParams[k] = String(v);
        nextFilterKeys.add(k);
      }
    });

    const serialized = JSON.stringify(nextParams);
    if (serialized === lastSyncedRef.current) return;
    lastSyncedRef.current = serialized;

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      // Clear managed params before re-writing them.
      next.delete('q');
      next.delete('page');
      prevFilterKeysRef.current.forEach((k) => next.delete(k));
      // Write current state.
      Object.entries(nextParams).forEach(([k, v]) => next.set(k, v));
      return next;
    }, { replace: true });

    prevFilterKeysRef.current = nextFilterKeys;
  }, [query, filters, page, setSearchParams]);

  return {
    initialQuery: initialQueryRef.current,
    initialFilters: initialFiltersRef.current as Record<string, any>,
    initialPage: initialPageRef.current,
  };
}
